import { createAsyncThunk } from '@reduxjs/toolkit';
import groupBy from 'lodash.groupby';

import { RootState } from '../../redux/root-reducer';
import { trackEcommerce } from '../../utils/analytics';
import { endpoints } from '../../utils/endpoints';
import { fetchJSON } from '../../utils/fetch';
import { logError } from '../../utils/logging';
import { hasSingleTicketSelected } from '../../utils/tickets';
import {
  AssignedTicketDetails,
  AssignedTickets,
} from '../assign-tickets/assign-tickets';
import { Answer } from '../registration-form/custom-form-factory-types';
import {
  submitFormAnswersAuthenticated,
  submitFormAnswersWithInviteId,
} from '../registration-form/registration-form-actions';
import {
  calculateOrderSummaryRequest,
  completeOrder,
  fetchInvites,
} from '../select-tickets/cart-thunk';
import {
  OrderSummaryPayloadType,
  TicketInvites,
} from '../select-tickets/cart-types';
import { executeCardPayment } from './stripe/stripe-utils';
import type { ConfirmCardPaymentType } from './stripe/use-stripe-payment';
import { BillingDetailsFieldValues } from './types';

const get = endpoints();

export const EventRegistrationStatus = {
  REGISTERED: 'registered',
  NOT_REGISTERED: 'not-registered',
  BLOCKED: 'blocked',
} as const;

export type EventRegistrationStatusTypes =
  typeof EventRegistrationStatus[keyof typeof EventRegistrationStatus];

export interface CompletePurchasePayload {
  makePayment: boolean;
  billingDetails: BillingDetailsFieldValues;
  answers?: Nullable<Answer[]>;
  confirmCardPayment?: ConfirmCardPaymentType;
  acceptedTerms?: boolean;
}

const CHECKOUT_FAILED_RESPONSE: {
  success: boolean;
  redirectToEvent: boolean;
  invitesSuccessful: boolean;
  errorMessage?: string;
} = {
  success: false,
  redirectToEvent: false,
  invitesSuccessful: false,
};

export const checkout = createAsyncThunk<
  {
    success: boolean;
    redirectToEvent: boolean;
    errorMessage?: string;
  },
  CompletePurchasePayload,
  { state: RootState }
>(
  'purchase/checkout',
  async (
    {
      makePayment,
      billingDetails,
      answers = null,
      confirmCardPayment,
      acceptedTerms = false,
    },
    { getState },
  ) => {
    const state = getState();
    CHECKOUT_FAILED_RESPONSE.errorMessage = undefined;

    const {
      eventDetails: {
        event: { url },
        widgetSource: source,
        widgetId,
      },
      cart: {
        promoCode,
        promoCodeStatus,
        selectedTickets,
        orderSummary: { orderId, total, lineItems },
      },
      authentication: { user, token },
      assignTickets: { selfAssignedTicketFieldsetId },
    } = state;

    const isLoggedIn = token !== null;
    const isSingleTicketRegistration = hasSingleTicketSelected(selectedTickets);

    const calculateSummaryBody: OrderSummaryPayloadType = {
      email: billingDetails.email,
      first_name: billingDetails.first_name,
      last_name: billingDetails.last_name,
      eventSlug: url,
      orderId,
      tickets: selectedTickets,
    };

    if (promoCodeStatus === 'valid') {
      calculateSummaryBody.promoCode = promoCode;
    }

    let calculateOrderSummaryResponse;
    try {
      calculateOrderSummaryResponse = await calculateOrderSummaryRequest(
        calculateSummaryBody,
      );
    } catch (e: any) {
      logError(e);
      CHECKOUT_FAILED_RESPONSE.errorMessage = 'Failed to update your order';

      if (e.response?.body?.errors) {
        CHECKOUT_FAILED_RESPONSE.errorMessage = e.response.body.errors[0];
      }
      return CHECKOUT_FAILED_RESPONSE;
    }

    const { id } = calculateOrderSummaryResponse;
    let status = 'succeeded';
    let stripeId = '';

    if (makePayment && confirmCardPayment) {
      const paymentResponse = await executeCardPayment(
        billingDetails.email,
        url,
        orderId,
        confirmCardPayment,
      );

      if (paymentResponse.status === 'failure') {
        CHECKOUT_FAILED_RESPONSE.errorMessage = 'Payment failed';
        return CHECKOUT_FAILED_RESPONSE;
      }

      status = paymentResponse.status;
      stripeId = paymentResponse.stripeId;
    }
    const { success, orderSlug, authRegistered } = await completeOrder({
      orderId: id,
      customerId: billingDetails.email,
      status,
      widgetId,
      source,
      eventSlug: url,
      stripeId,
      acceptedTerms,
      token: isSingleTicketRegistration && token ? token : undefined,
    });

    if (!success) {
      CHECKOUT_FAILED_RESPONSE.errorMessage = 'Failed to complete order';
      return CHECKOUT_FAILED_RESPONSE;
    }

    let invites = [];

    if (isSingleTicketRegistration) {
      if (!isLoggedIn) invites = await fetchInvites(url, orderSlug);
    } else {
      invites = await assignTickets(
        url,
        orderSlug,
        state.assignTickets.tickets,
      );
    }

    const invitesSuccessful =
      Boolean(invites.length) || isSingleTicketRegistration;

    if (answers?.length) {
      const isSelfAssignedBulkPurchase = Boolean(
        state.assignTickets.tickets.length > 1 &&
          selfAssignedTicketFieldsetId &&
          user?.email,
      );

      let inviteId = null;
      if (isSingleTicketRegistration) {
        inviteId = invites[0]?.invites[0]?.id;
      } else if (isSelfAssignedBulkPurchase) {
        inviteId = getInviteIdForBulkPurchase(
          invites,
          selfAssignedTicketFieldsetId,
          user?.email || '',
        );
      }

      try {
        const useAuthenticatedEndpoint =
          authRegistered && !isSelfAssignedBulkPurchase;
        if (useAuthenticatedEndpoint && token) {
          await submitFormAnswersAuthenticated(url, answers, token);
        } else if (inviteId) {
          await submitFormAnswersWithInviteId(url, inviteId, answers, token);
        }
      } catch (e) {
        // Do nothing if form answers have failed
      }
    }

    if (success && state.eventDetails.googleAnalyticsCode) {
      trackEcommerce({
        orderId,
        total,
        tickets: lineItems,
        promoCode: state.cart.promoCode,
        eventCurrency: state.eventDetails.event.currency,
      });
    }

    return {
      success,
      redirectToEvent:
        isLoggedIn && isSingleTicketRegistration && authRegistered,
      invitesSuccessful,
    };
  },
);

const assignTickets = async (
  eventSlug: string,
  orderSlug: string,
  assignedTickets: AssignedTickets,
): Promise<TicketInvites[]> => {
  if (!assignedTickets.length) return [];

  const grouped = groupBy(assignedTickets, 'ticketId');
  const assignedTicketsTuples = Object.entries(grouped);

  const tickets = assignedTicketsTuples.map(
    ([ticketId, invites]: [string, AssignedTicketDetails[]]) => ({
      external_id: ticketId,
      invites: invites.map(({ first_name, last_name, email }) => ({
        email,
        last_name,
        first_name,
      })),
    }),
  );

  try {
    const invites = await fetchJSON(
      get('ORDER_INVITES')(eventSlug, orderSlug),
      {
        method: 'PUT',
        body: {
          tickets,
        },
      },
    );

    return invites;
  } catch (e) {
    return [];
  }
};

const getInviteIdForBulkPurchase = (
  invites: TicketInvites[],
  selfAssignedTicketFieldsetId: Nullable<string>,
  email: string,
) => {
  if (!selfAssignedTicketFieldsetId) return null;

  const ticketId = selfAssignedTicketFieldsetId.split('__')[0];

  const invite = invites
    .find(({ ticket_external_id }) => ticketId === ticket_external_id)
    ?.invites.find(invite => invite.email === email);

  return invite?.id || null;
};
